import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AgentReasoningEffort,
  reasoningEffortOptions,
} from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { cn } from "@/utils";
import { trackComposerControlChanged } from "@/utils/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";

interface ReasoningEffortOption {
  value: AgentReasoningEffort;
  label: string;
  description: string;
  pillColor: string;
}

const EXTREME_OPTION: ReasoningEffortOption = {
  value: "x-high",
  label: "Extreme",
  description: "Maps to latest Claude Opus model at medium reasoning",
  pillColor:
    "text-fuchsia-600 dark:text-fuchsia-400 bg-fuchsia-500/10 hover:bg-fuchsia-500/15",
};

const BASE_OPTIONS: ReasoningEffortOption[] = [
  {
    value: "high",
    label: "High",
    description: "Maximum depth — complex canvas work, in-depth research",
    pillColor:
      "text-violet-600 dark:text-spectrum-violet bg-spectrum-violet/10 hover:bg-spectrum-violet/15",
  },
  {
    value: "standard",
    label: "Standard",
    description: "Everyday work — diagrams, presentations, structured content",
    pillColor: "text-muted-foreground hover:text-foreground hover:bg-accent",
  },
  {
    value: "low",
    label: "Low",
    description: "Simple tasks — fetch, place, quick edits",
    pillColor:
      "text-lc-warning dark:text-lc-warning bg-lc-warning/10 hover:bg-lc-warning/15",
  },
];

const STANDARD_OPTION = BASE_OPTIONS.find((o) => o.value === "standard")!;
const OPTIONS_BY_VALUE = new Map(
  [EXTREME_OPTION, ...BASE_OPTIONS].map((o) => [o.value, o] as const),
);

interface ReasoningEffortButtonProps {
  effort: AgentReasoningEffort;
  onChange: (effort: AgentReasoningEffort) => void;
}

export function ReasoningEffortButton({
  effort,
  onChange,
}: ReasoningEffortButtonProps) {
  const [open, setOpen] = useState(false);
  const flags = useFlags<Flags>();

  const options = reasoningEffortOptions(flags).map(
    (value) => OPTIONS_BY_VALUE.get(value)!,
  );

  const activeOption =
    options.find((o) => o.value === effort) ?? STANDARD_OPTION;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "h-7 rounded-full px-2 gap-1 text-xs font-medium transition-colors shrink-0",
                  activeOption.pillColor,
                )}
                aria-label={`Thinking effort: ${activeOption.label}`}
              >
                {activeOption.label}
                <ChevronDown className="h-3 w-3 shrink-0" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Set thinking effort</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-72 p-2 rounded-[14px] border"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {options.map((option) => (
          <button
            key={option.value}
            onClick={() => {
              if (option.value !== effort) {
                trackComposerControlChanged({
                  control: "reasoning_effort",
                  value: option.value,
                });
              }
              onChange(option.value);
              setOpen(false);
            }}
            className={cn(
              "w-full flex items-center gap-3 min-h-11 px-4 py-2 rounded-lg text-left transition-colors",
              "hover:bg-accent",
              effort === option.value && "bg-accent/60",
            )}
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm">{option.label}</div>
              <div className="text-xs text-muted-foreground leading-snug">
                {option.description}
              </div>
            </div>
            {effort === option.value && (
              <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
